import React, { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';

import makeStyles from '@mui/styles/makeStyles';
import { TableSortLabel } from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { ReactComponent as ColumnSortIcon } from 'assets/icons/ColumnSort.svg';
import { ReactComponent as ColumnSortIconAsc } from 'assets/icons/ColumnSortActiveAsc.svg';
import { ReactComponent as ColumnSortIconDesc } from 'assets/icons/ColumnSortActiveDesc.svg';
import { GlobalState } from 'reducers/stateTypes';
import ColumnFilter from 'components/Common/ColumnFilter';
import { ContactNotePartner, ContactNoteRelatedBR } from 'types/ContactNote';
import { isDigit } from 'utils/stringUtil';

type IconComponentType = React.FunctionComponent<{ className: string }>;

export const tableStyles = makeStyles(() => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  activeSortIcon: {
    opacity: 1,
    size: '1%',
  },
  inactiveSortIcon: {
    opacity: 0.2,
    height: '1%',
  },
  filterContainer: {
    position: 'relative',
    display: 'inline-flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
}));

export type Order = 'asc' | 'desc' | '';

type MyObject = any[] | string;

export enum HeadCellType {
  SORT = 'sort',
  FILTER = 'filter',
  FILTER_NO_TEXT_TRANSFORM = 'filter_no_transform',
  EMPTY = '',
}

export interface HeadCell<MyObject> {
  disablePadding?: boolean;
  id: keyof MyObject | string;
  label: string;
  numeric?: boolean;
  type: HeadCellType;
  customValues?: any;
}

interface EnhancedTableProps<ObjectType> {
  headCells: HeadCell<ObjectType>[];
  order: Order;
  orderBy: string | keyof ObjectType;
  setOrder: Dispatch<SetStateAction<any>>;
  setOrderBy: Dispatch<SetStateAction<any>>;
  headCellContext: HeadCellContext;
  columnWidths?: string[];
  filters?: object;
  entities?: any[];
  setFilters?: any;
}

export enum HeadCellContext {
  PARTNERS = 'PARTNERS',
  CONTACT_NOTES = 'CONTACT_NOTES',
  ADDITIONAL_ATTENDEES = 'ADDITIONAL_ATTENDEES',
  OTHER = 'OTHER',
}

export function DeprecatedTableHead(props: EnhancedTableProps<MyObject>) {
  const { order, orderBy, setOrder, setOrderBy } = props;
  const contactNotes = useSelector((state: GlobalState) => state.contactNotes);
  const classes = tableStyles();

  const getEntities = () => {
    if (props.headCellContext === HeadCellContext.CONTACT_NOTES) {
      return contactNotes;
    } else {
      return props.entities || [];
    }
  };

  const entities = getEntities();

  const onRequestSort = (event: any, property: any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getIconComponent = (id: any) => {
    if (orderBy === id) {
      return order === 'asc' ? ColumnSortIconAsc : ColumnSortIconDesc;
    } else {
      return ColumnSortIcon;
    }
  };

  const createSortHandler = (property: any) => (event: any) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        {props.headCells.map((headCell, index) => (
          <TableCell
            key={String(headCell.id)}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : undefined}
            sortDirection={orderBy === headCell.id ? order || false : false}
            id={`tablehead-${String(headCell.id)}`}
            style={props.columnWidths && { width: props.columnWidths[index] }}
          >
            {headCell.type === HeadCellType.FILTER || headCell.type === HeadCellType.FILTER_NO_TEXT_TRANSFORM ? (
              <div className={classes.filterContainer}>
                {headCell.label}
                <ColumnFilter
                  values={Array.from(
                    new Set(
                      entities.map((entity: any) => {
                        if (Array.isArray(entity[headCell.id])) {
                          if (headCell.id === 'relatedPartners') {
                            return entity[headCell.id]?.map((item: ContactNotePartner) => item.fullName).join(', ');
                          }
                          if (headCell.id === 'relatedBRs') {
                            return entity[headCell.id]
                              ?.map((item: ContactNoteRelatedBR) => {
                                if (isDigit(item.masterNumber)) {
                                  return `${item.masterNumber?.slice(0, 4)}.${item.masterNumber?.slice(4)}`;
                                }
                                return item.masterNumber || 'Prospecting BR';
                              })
                              .join(', ');
                          }
                          return entity[headCell.id].join(', ');
                        }
                        if (headCell.id === 'createdByFullName') {
                          return `${entity[headCell.id] || 'N/A'} (${entity?.createdBy})`;
                        }

                        return entity[headCell.id];
                      }),
                    ),
                  )}
                  name={headCell.id as unknown as string}
                  context={props.headCellContext}
                  disableFormat={headCell.type === HeadCellType.FILTER_NO_TEXT_TRANSFORM}
                  filter={props.filters}
                  setFilters={props.setFilters}
                  customValues={headCell.customValues}
                />
              </div>
            ) : headCell.type === HeadCellType.SORT ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order || 'asc' : 'asc'}
                onClick={createSortHandler(headCell.id)}
                IconComponent={getIconComponent(headCell.id) as IconComponentType}
              >
                {headCell.label}
              </TableSortLabel>
            ) : (
              <></>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default DeprecatedTableHead;
